import { KEYS } from 'app/constants/SceneConstants';
import { StageStartDisplay } from '../display/ui/StageStartDisplay';
import { CharacterBase } from '../game/characters/CharacterBase';
import { CharacterHero } from '../game/characters/CharacterHero';
import { EnemyController } from '../game/EnemyController';
import { GameCamera } from '../game/GameCamera';
import { GameEvents } from '../game/GameEvents';
import { LevelMap } from '../game/LevelMap';
import { WorldProjectile } from '../game/world/objects/WorldProjectile';
import { WorldRenderer } from '../game/world/WorldRenderer';
import { TouchButtons } from '../input/TouchButtons';
import { TouchPad } from '../input/TouchPad';
import { KeyboardController } from '../input/KeyboardController';
import ShortcutController from '../shortcuts/ShortcutController';
import SceneBaseTT from './SceneBaseTT';
import { VIEWPORT_EVENTS, AssetUnloader } from '@jollywise/jollygoodgame';
import { CollisionMap } from '../game/world/collisions/CollisionMap';
import { GameHud } from '../display/GameHud';
import { EnemyHealthBar } from '../display/EnemyHealthBar';

const ASSETPACK = require('../../../assets/assetpack.json');

export default class GamePlay extends SceneBaseTT {
  constructor(config = { key: KEYS.GamePlay, active: false }) {
    super(config);

    this._levelAssetsLoaded = false;
  }

  init(config) {
    this._leveldata = config.data;
    this._stageConfig = this.game.model.getStageConfig(this._leveldata.stageid);
  }

  preload() {
    this.game.hud.setState('EMPTY');
    const info = this.game.model.getStoryStagePosition(this._leveldata.stageid);
    this._levelIndex = info?.level;
    if (info.level != this._levelAssetsLoaded) {
      // unload previous level assets
      if (this._levelAssetsLoaded !== false) {
        AssetUnloader.UnloadAssetpack(this, ASSETPACK.levels[this._levelAssetsLoaded]);
        this._levelAssetsLoaded = false;
      }
      // // load new level assets
      this._levelAssetsLoaded = info.level.toString();
      Object.keys(ASSETPACK.levels[this._levelAssetsLoaded]).forEach((key) =>
        this.load.addPack(ASSETPACK.levels[this._levelAssetsLoaded], key)
      );
    }
  }

  create() {
    super.create();
    this.setBackgroundColour();
    this.game.soundController.playMusic('level_' + (this._levelIndex ? this._levelIndex : '1'));
    this.game.setShortcuts(ShortcutController.STATES.INGAME);
    this.game.hud.setState('GAMEPLAY');
    this._started = false;
    this._sfxTriggered = {};
    this.frameTrackng = { time: 0, frames: 0 };

    if (this.cache.json.get(this._stageConfig.mapid)) {
      this._map = new LevelMap(this, this.cache.json.get(this._stageConfig.mapid));

      this._worldCollisions = new CollisionMap(this);
      this._worldRenderer = new WorldRenderer(this, this._map, this._worldCollisions.groups);
      this._hero = new CharacterHero(
        this,
        this._map.heroSpawn.x,
        this._map.heroSpawn.y,
        this.game.model.getHeroConfig(this._leveldata.heroid),
        this._worldCollisions
      );
      this._worldRenderer.addHeroCharacter(this._hero);
      this._cameraController = new GameCamera(this, this._map);
      this._enemyController = new EnemyController(this, this._worldRenderer, this._worldCollisions);
      this._enemyController.setHeroTarget(this._hero);
      this._enemyController.setCombatZones(this._map.combatzones);
      this._enemyController.setEnemySpawns(this._map.enemySpawns);

      this.viewport = this._cameraController.update(0);

      // controller
      if (this.game.isDesktop) {
        this._heroController = new KeyboardController(this, this._hero);
      } else {
        // mobile controls
        this._heroController = new TouchPad(this, this._hero);
        this._heroController.x =
          this.game.viewportController.leftPadded + this._heroController.displayWidth * 0.5;
        this._heroController.y =
          this.game.viewportController.bottomPadded - this._heroController.displayHeight * 0.5;

        this.touchButtons = new TouchButtons(this, this._hero);
        this.touchButtons.x =
          this.game.viewportController.rightPadded - this.touchButtons.displayWidth;
        this.touchButtons.y = this.game.viewportController.bottom - this.touchButtons.displayHeight;
      }

      this.vignette = this.add.image(0, 0, 'game_graphics', 'game_vignette');
      this.vignette.x = this.game.viewportController.left - 10;
      this.vignette.y = this.game.viewportController.top - 10;
      this.vignette.displayWidth = this.game.viewportController.width + 20;
      this.vignette.displayHeight = this.game.viewportController.height + 20;
      this.vignette.setScrollFactor(0);
      this.vignette.setOrigin(0);
      this._gamehud = new GameHud(this);
      this._gamehud.setScrollFactor(0);
      this._gamehud.setHero(this._hero);
      this._enemyHealth = new EnemyHealthBar(this);
      this._enemyHealth.setScrollFactor(0);
      this.game.viewportController.events.on(
        VIEWPORT_EVENTS.UPDATED,
        this.handleViewportUpdate,
        this
      );
      this.handleViewportUpdate();
      this.showStage();

      const info = this.game.model.getStoryStagePosition(this._leveldata.stageid);
      this.game.soundController.playMusic('level_' + (info ? info.level : '1'));
    }

    this._timeRemaining = this._stageConfig.timeLimit;
    this._livesRemaining = 3;
    this._gamehud.setHeroLives(this._livesRemaining);
    this._state = GamePlay.STATES.INIT;
    this._score = {
      damageDealt: 0,
      coinsCollected: 0,
    };

    // event listeners
    this.addEventListeners();
    this.input.addPointer(3);

    this.game.stage = this;
    this.game.hud.events.on('STATE_CHANGE', this.handleHudChange, this);

    if (__WATCH__) this._debugGraphics = this.add.graphics();
  }

  shake(duration = 50, strength = 0.01) {
    this.cameras.main.shake(duration, strength);
  }

  equipHero(weapon) {
    const weaponconfig = this.game.model.getWeaponConfig(weapon);
    const activeskin = this.game.player.getActiveWeaponSkin(weapon);
    const skinconfig = weaponconfig.skins.find((s) => s.skin === activeskin);
    if (weaponconfig) {
      this._hero.addWeapon({
        uses: skinconfig?.uses || weaponconfig.uses,
        skin: skinconfig?.skin || weaponconfig.skin,
        damage: skinconfig?.damage || weaponconfig.damage,
        damagesfx: weaponconfig.damagesfx,
        animation: skinconfig?.animation || weaponconfig.animation,
      });
    }
  }

  handleViewportUpdate() {
    if (this.touchButtons) {
      this._heroController.x =
        this.game.viewportController.leftPadded + this._heroController.displayWidth * 0.5;
      this._heroController.y =
        this.game.viewportController.bottomPadded - this._heroController.displayHeight * 0.5;
      this.touchButtons.x =
        this.game.viewportController.rightPadded - this.touchButtons.displayWidth;
      this.touchButtons.y = this.game.viewportController.bottom - this.touchButtons.displayHeight;
    }
    this._cameraController.updateCameraBounds();
    this.vignette.x = this.game.viewportController.left - 10;
    this.vignette.y = this.game.viewportController.top - 10;
    this.vignette.displayWidth = this.game.viewportController.width + 20;
    this.vignette.displayHeight = this.game.viewportController.height + 20;
    this.vignette.setScrollFactor(0);
    this.vignette.setOrigin(0);

    const halfPadding = this.game.viewportController.padding * 0.5;
    this._gamehud.x =
      this.game.viewportController.leftPadded + this._gamehud.displayWidth * 0.5 + halfPadding;
    this._gamehud.y =
      this.game.viewportController.topPadded + this._gamehud.displayHeight * 0.5 + halfPadding;

    this._enemyHealth.x = this.game.viewportController.right - 135;
    this._enemyHealth.y = this.game.viewportController.padding;
  }

  addEventListeners() {
    this.events.on(EnemyController.COMBAT_ZONE_START, this.handleCombatZoneStart, this);
    this.events.on(EnemyController.COMBAT_ZONE_END, this.handleCombatZoneEnd, this);
    this.events.on(EnemyController.ENEMY_DEFEATED, this.handleEnemyDefeated, this);
    this.events.on(GameEvents.DEAL_DAMAGE, this.handleHeroDamage, this);
    this.events.on(GameEvents.PICKUP_ITEM, this.handlePickupItem, this);
    this.events.on(GameEvents.COLLECT_COIN, this.handleCoinCollected, this);
    this.events.on(GameEvents.EXIT_STAGE, this.handleExitLevel, this);
    this.events.on(GameEvents.CHECKPOINT, this.handleEnteredCheckpoint, this);
    this.events.on(GameEvents.SPAWN_PROJECTILE, this.handleSpawnProjectile, this);
    this.events.on(GameEvents.DESTROYED_OBJECT, this.handleDestructableDestroyed, this);
    this.events.on(GameEvents.PLAYER_ATTACK_HIT, this.handlePlayerAttackHit, this);
    this.events.on(GameEvents.PLAY_LIVES_EXPIRED, this.failLevel, this);
  }

  removeEventListeners() {
    this.events.off(EnemyController.COMBAT_ZONE_START, this.handleCombatZoneStart, this);
    this.events.off(EnemyController.COMBAT_ZONE_END, this.handleCombatZoneEnd, this);
    this.events.off(EnemyController.ENEMY_DEFEATED, this.handleEnemyDefeated, this);
    this.events.off(GameEvents.DEAL_DAMAGE, this.handleHeroDamage, this);
    this.events.off(GameEvents.PICKUP_ITEM, this.handlePickupItem, this);
    this.events.off(GameEvents.COLLECT_COIN, this.handleCoinCollected, this);
    this.events.off(GameEvents.EXIT_STAGE, this.handleExitLevel, this);
    this.events.off(GameEvents.CHECKPOINT, this.handleEnteredCheckpoint, this);
    this.events.off(GameEvents.SPAWN_PROJECTILE, this.handleSpawnProjectile, this);
    this.events.off(GameEvents.DESTROYED_OBJECT, this.handleDestructableDestroyed, this);
    this.events.off(GameEvents.PLAYER_ATTACK_HIT, this.handlePlayerAttackHit, this);
    this.events.off(GameEvents.PLAY_LIVES_EXPIRED, this.failLevel, this);
  }

  get runningScore() {
    return this._score.damageDealt + this._score.coinsCollected;
  }

  get enemies() {
    return this._enemyController;
  }

  get hero() {
    return this._hero;
  }

  get collisionMap() {
    return this._worldCollisions;
  }

  // =============================================================================================
  // START STAGE
  // =============================================================================================

  showStage() {
    this.stageDisplay = new StageStartDisplay(this, {
      heroid: this._leveldata.heroid,
      stageInfo: this.game.model.getStoryStagePosition(this._leveldata.stageid),
    });
    this.tweens.add({
      targets: this.stageDisplay,
      alpha: 0,
      delay: 1500,
      duration: 500,
      onComplete: this.start,
      onCompleteScope: this,
    });
  }

  start() {
    this._started = true;
    this._state = GamePlay.STATES.FIGHTING;
    this._heroController.enable();
    if (this.touchButtons) this.touchButtons.enable();
    this._cameraController.followCharacter(this._hero);
  }

  get hasReachedLevelEnd() {
    return this.viewport.right >= this._map.exit;
  }

  // =============================================================================================
  // MAIN LOOP
  // =============================================================================================

  update(time, delta) {
    this.viewport = this._cameraController.update(delta);
    // this.vignette.displayWidth = this.viewport.width;
    //  this.vignette.x = this.viewport.x;

    delta /= 1000;

    switch (this._state) {
      case GamePlay.STATES.FIGHTING:
        this.updateStateFighting(delta);
        break;
      case GamePlay.STATES.RESPAWNING:
        this.updateStateRespawn(delta);
        break;
      case GamePlay.STATES.EXITING:
        this.updateStateExit(delta);
        break;
    }
  }

  handleHudChange() {
    // this._enemyHealth = this.game.hud.getComponent('enemy_health');

    if (this._gamehud) {
      this._gamehud.setHeroLives(this._livesRemaining);
      this._gamehud.updateScore(this.runningScore);
    }
  }

  updateStateFighting(delta) {
    this.keepHeroInViewport();
    this._hero.setDirection(this._heroController.getMovementDirection());
    this._worldCollisions.update(delta);
    this._worldRenderer.update(delta);
    if (!this._enemyController.hasActiveEnemies)
      this._worldCollisions.checkWorldTriggers(this._hero.bodyCollider);
    this._hero.update(delta);
    this._enemyController.update(delta, this.viewport);
    this._gamehud.update(delta);
    this._enemyHealth.target = this._hero.getLastAttackTarget();
    this._enemyHealth.setBoss(this._enemyController.activeBoss);
    if (this._timeRemaining > 0) this._timeRemaining = Math.max(0, this._timeRemaining - delta);
    this._enemyHealth.update(delta);
    if (this._hero.isDead && !this._hero.isRespawning) return this.respawnHero();

    // are we at the end of the level
    if (
      !this._enemyController.hasActiveEnemies &&
      this.viewport.right >= this._map.exit &&
      !this._cameraController.zoneLocked
    ) {
      this._cameraController.lockToZone(this.viewport);
      this.showProgressInidicator(true);
    }

    this.updateProgressIndicator();
  }

  updateStateExit(delta) {
    this._hero.update(delta);
    if (!this._cameraController.isInViewport(this._hero)) this.completeLevel();
  }

  updateStateRespawn(delta) {
    if (!this._hero.isDead && !this._hero.isInState(CharacterBase.STATES.RESPAWN)) {
      this._state = GamePlay.STATES.FIGHTING;
    } else {
      this._hero.update(delta);
    }
  }

  respawnHero() {
    this._livesRemaining--;
    this._state = GamePlay.STATES.RESPAWNING;
    this.time.addEvent({
      delay: 2500,
      callback: () => {
        if (this._livesRemaining) {
          this.hero.respawn();
          this._gamehud.setHeroLives(this._livesRemaining);
        } else {
          this.failLevel();
        }
      },
      callbackScope: this,
    });
  }

  keepHeroInViewport() {
    if (this._hero.y > this.viewport.y + this.viewport.height)
      this._hero.y = this.viewport.y + this.viewport.height;
    if (this._hero.x < this.viewport.x) this._hero.x = this.viewport.x;
    if (this._hero.x > this.viewport.x + this.viewport.width)
      this._hero.x = this.viewport.x + this.viewport.width;
  }

  // =============================================================================================
  // EVENT HANLDERS
  // =============================================================================================

  handleHeroDamage({ target, damage }) {
    this._score.damageDealt += damage * this.game.model.pointsPerDamageDealt;
    this._gamehud.updateScore(this.runningScore);
  }

  handleCoinCollected(coin) {
    this._score.coinsCollected += this.game.model.pointsPerCoin;
    this._gamehud.updateScore(this.runningScore);
    this.game.player.addCoins(1);
    this.game.soundController.playSFX('sfx', 'coin_pickup');
  }

  handlePickupItem(pickup) {
    let config;
    switch (pickup.type) {
      case 'weapon':
        this.equipHero(pickup.id);
        config = this.game.model.getWeaponConfig(pickup.id);
        break;
      default:
        config = this.game.model.getPickupConfig(pickup.id);
        if (config?.health) this._hero.heal(config.health);
        break;
    }
    if (config?.pickupsfx) this.game.soundController.playSFX('sfx', config.pickupsfx);
  }

  handleDestructableDestroyed(destructable) {
    const objectspawn = { ...destructable.bodyCollider?.center } || {
      x: destructable.x,
      y: destructable.y,
    };

    objectspawn.y = Math.max(objectspawn.y, 410);

    if (destructable.weapon) {
      this._worldRenderer.spawnWeapon(destructable.weapon, objectspawn.x, objectspawn.y);
    }
    if (destructable.coins)
      this._worldRenderer.spawnCoins(destructable.coins, objectspawn.x, objectspawn.y);
    if (destructable.pickup)
      this._worldRenderer.spawnPickup(destructable.pickup, objectspawn.x, objectspawn.y);
    if (destructable.destroyAnim) {
      const anim = this.add.spine(objectspawn.x, objectspawn.y, 'effects');
      anim.once('complete', () => anim.destroy());
      anim.play(destructable.destroyAnim);
    }
  }

  handlePlayerAttackHit(config) {
    if (!this.attackFX) this.attackFX = this.add.spine(config.x, config.y, 'effects');
    const rdm = Math.ceil(Math.random() * 6);
    this.attackFX.visible = true;
    this.attackFX.setPosition(config.x, config.y);
    this.attackFX.once('complete', () => (this.attackFX.visible = false));
    this.attackFX.play('hit_' + rdm, false);
    this.attackFX.setScale(config.dir || 1, 1);
  }

  handleExitLevel() {
    this.removeEventListeners();

    this._state = GamePlay.STATES.EXITING;
    this._cameraController.stopFollowing();
    this._hero.setDirection({ x: 1, y: 0 });
  }

  handleSpawnProjectile(config) {
    this._worldRenderer.spawnProjectile(config);
  }

  // =============================================================================================
  // ENEMY CONTROLLER EVENTS
  // =============================================================================================

  handleCombatZoneStart(zone) {
    this._cameraController.lockToZone(zone);
  }

  handleCombatZoneEnd(zone) {
    this.showProgressInidicator(true);
    this._cameraController.releaseZoneLock();
  }

  updateProgressIndicator() {
    if (!this.progressionArrow?.visible) {
      // are we at the end of the level
      this.levelend =
        !this._enemyController.hasActiveEnemies && this.viewport.right >= this._map.exit;
      if (this.levelend) {
        this._cameraController.minLeft = this.viewport.left;
        this.showProgressInidicator(true);
      }
    }

    if (!this.levelend && this.hero.x > this.progressionArrow?.x) {
      this.showProgressInidicator(false);
    }
  }

  showProgressInidicator(value) {
    if (value && !this.progressionArrow) {
      this.progressionArrow = this.add
        .image(0, 0, 'graphics', 'combatzone_end_graphic')
        .setOrigin(0);
      this.progressionArrow.x = this.viewport.right - this.progressionArrow.width;
      this.progressionArrow.y = this.viewport.top;
      this.progressionArrow.depth = 100;
      this.tweens.add({
        targets: this.progressionArrow,
        alpha: 0,
        duration: 400,
        repeat: Infinity,
        hold: 100,
        repeatDelay: 100,
        yoyo: true,
      });
    }

    this.progressionArrow.x = this.viewport.right - this.progressionArrow.width;
    if (this.progressionArrow) this.progressionArrow.visible = value;

    // if (value && !this.progressIndicator) {
    //   this.progressIndicator =
    //     this.progressIndicator || this.add.image(0, 0, 'graphics', 'combatzone_end_graphic');
    //   this.progressIndicator.x = this.viewport.right - 200;
    //   this.progressIndicator.y = this.viewport.top + 200;
    //   this.progressIndicator.alpha = 0;
    //   this.tweens.add({
    //     targets: this.progressIndicator,
    //     alpha: 1,
    //     duration: 400,
    //     repeat: 5,
    //     hold: 100,
    //     repeatDelay: 100,
    //     yoyo: true,
    //   });
    // } else if (this.progressIndicator && !value) {
    //   this.progressIndicator.destroy();
    //   this.progressIndicator = null;
    // }
  }

  handleEnemyDefeated(enemy) {
    const config = this.game.model.getEnemy(enemy.id);
    if (config?.coins) this._worldRenderer.spawnCoins(config.coins, enemy.x, enemy.y);
  }

  handleEnteredCheckpoint(checkpoint) {
    this._checkpoint = { x: this._hero.x, y: this._hero.y };
  }

  playSFX(sfx, { sheet, lastPlayedMin } = {}) {
    if (lastPlayedMin) {
      if (!this._sfxTriggered[sfx] || this.time.now - this._sfxTriggered[sfx] > lastPlayedMin) {
        this._sfxTriggered[sfx] = this.time.now;
        this.game.soundController.playSFX(sheet || 'sfx', sfx);
      }
    } else this.game.soundController.playSFX(sheet || 'sfx', sfx);
  }

  // =============================================================================================
  // STAGE COMPLETE
  // =============================================================================================

  failLevel() {
    this.game.failStage();
  }
  completeLevel() {
    this.game.completeStage({
      damage: this._score.damageDealt,
      coins: this._score.coinsCollected,
      time: Math.ceil(this._timeRemaining) * this.game.model.pointsPerSecondsRemaining,
      lives: this._livesRemaining * this.game.model.pointsPerLivesRemaining,
    });
  }

  shutdown() {
    this.game.viewportController.events.off(
      VIEWPORT_EVENTS.UPDATED,
      this.handleViewportUpdate,
      this
    );

    this.game.hud.events.off('STATE_CHANGE', this.handleHudChange, this);
    this.removeEventListeners();
    this.game.stage = null;
    this._state = null;
    this._hero = null;
    this._worldCollisions = null;
    this._cameraController = null;
    this._enemyController = null;
    this._worldRenderer = null;
    this.progressionArrow = null;
    this._map = null;
    this.attackFX = null;
    super.shutdown();
  }
}

GamePlay.STATES = {
  INIT: 'init',
  FIGHTING: 'fighting',
  EXITING: 'exiting',
  RESPAWNING: 'respawning',
};
